import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpErrorResponse, HttpHeaders, HttpClient } from '@angular/common/http';
import { ApiService } from '../../api/api.service';
import { LoggerService } from '../../logger-services/logger.service';
import { ConfigurationService } from '../../configuration/configuration.service';
import { FilterFromRetrievedFilesResponse } from '../../../app/models/file-upload.models';
import { environment } from '../../../environments/environment';
import { TokenService } from '../../../shared/services/token/token.service';

@Injectable({
  providedIn: 'root'
})
/**
 * Service for filtering previously retrieved user files using a search keyword.
 *
 * This wraps the backend endpoint configured as 'FILTER_FROM_RETRIVED_FILES',
 * centralizing API invocation, logging, and error formatting for consumers.
 */
export class FilterFromRetrievedFilesService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);
  private readonly loggerService = inject(LoggerService);
  private readonly configService = inject(ConfigurationService);
  private readonly tokenService = inject(TokenService);
  
  /**
   * Gets the configuration data from the configuration service
   */
  private get configdata(): any {
    return this.configService.configData;
  }

    /**
   * Get CORS-compliant headers for presigned URF URL APIs
   */
  private getCorURFHeaders(token?: string): HttpHeaders {
    let headers = new HttpHeaders({
      'content-type': 'application/json',
      'user-id': (environment.LoginUserID == undefined || environment.LoginUserID == null) ? "" : environment.LoginUserID,
      'tenant-id': environment.TenantId || '0',
      'domain-name': environment.DomainName || '',
    });

    // Add user full name if available
    if (environment.LoginUserName) {
      headers = headers.set('x-user-full-name', environment.LoginUserName);
    }

    // Add authorization token if provided
    if (token) {
      headers = headers.set('authorization', `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * Filters uploaded/retrieved files by a keyword.
   *
   * - Builds the API endpoint from configuration
   * - Sends a GET request and returns typed response
   * - Logs and surfaces HTTP errors in a consistent format
   *
   * @param keyword - The search keyword entered by the user
   * @returns Observable emitting the filtered files response
   */
  FilterFromRetrievedFiles(keyword: string): Observable<FilterFromRetrievedFilesResponse> {
    try {
      let endpoint = this.apiService.getApiUrlFromConfig('FILTER_FROM_RETRIVED_FILES');
      
      const requestPayload: any = {keyword:keyword};
    
      // Use direct HTTP call with CORS-compliant headers
      const url = `${this.configdata.docFileUploadUrl}${endpoint}`;
      const headers = this.getCorURFHeaders(this.tokenService.getAccessToken());

      return this.http.post<FilterFromRetrievedFilesResponse>(url, requestPayload, {headers}).pipe(
        catchError((error: HttpErrorResponse) => {
          this.loggerService.logError(this.formatError(error), "FilterFromRetrievedFiles");
          return throwError(() => error);
        })
      );
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), "FilterFromRetrievedFiles");
      return throwError(() => error);
    }
  }

  /**
   * Formats error object for logging and downstream consumption
   * @param error - Raw error from HttpClient or other sources
   * @returns A normalized error object
   */
  private formatError(error: any): any {
    if (error instanceof HttpErrorResponse) {
      return {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        error: error.error
      };
    }
    return error;
  }
}
